// CLOSEOUT1 (docs/build-briefs/CLOSEOUT1-closeout-redesign.md): which closeout checks need a look and which passed. Pure and
// DOM-free, over fields the closeout API already returns. A check that needs a look becomes an exception row; the rest become
// one compact checklist. Neither open tabs nor a failed payment blocks finalizing (an open tab carries over, a failed payment
// needs a fresh charge), so the words say "review", never "blocked".

import type { CloseoutSummaryDto } from "./api.ts";
import { formatCents } from "./money.ts";

export type CloseoutCheckKey =
  | "open_tabs"
  | "unpriceable"
  | "failed_payments"
  | "pending_payments"
  | "outside_collections"
  | "voids"
  | "reopened"
  | "readers"
  | "sync"
  | "settlement";

/** pass: a tick. info: a fact worth a glance that is not a problem (voids happened). fail: an exception row. */
export type CloseoutCheckState = "pass" | "info" | "fail";
export type CloseoutCheckTone = "bad" | "warn" | "info";

export interface CloseoutCheck {
  key: CloseoutCheckKey;
  label: string;
  state: CloseoutCheckState;
  /** The value shown beside the tick: "0", "2 open", "$12.00 recorded". */
  value: string;
  /** The phrase used in the one-line "N other checks passed" summary. */
  phrase: string;
  /** For a fail: the chip colour, the chip word, one plain line, and the sentence the verdict may borrow. */
  tone?: CloseoutCheckTone;
  chipText?: string;
  title?: string;
  detail?: string;
  verdictSentence?: string;
  /** True when the check is not a problem but its detail rows should still be shown (recorded outside collections). */
  showDetail?: boolean;
}

export interface CloseoutVerdict {
  failCount: number;
  tone: "good" | "warn" | "bad";
  chipSymbol: string;
  chipText: string;
  title: string;
  help: string;
}

const NUMBER_WORDS = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function buildCloseoutChecks(summary: CloseoutSummaryDto): CloseoutCheck[] {
  const checks: CloseoutCheck[] = [];

  const openTabs = summary.openTabsAtDayEnd;
  const owed = openTabs.reduce((sum, tab) => sum + tab.owedWithTaxCents, 0);
  checks.push(
    openTabs.length === 0
      ? { key: "open_tabs", label: "Open tabs", state: "pass", value: "0", phrase: "no open tabs" }
      : {
          key: "open_tabs",
          label: "Open tabs",
          state: "fail",
          value: String(openTabs.length),
          phrase: "open tabs",
          tone: "warn",
          chipText: "Open tabs",
          title: `${count(openTabs.length, "open tab", "open tabs")}, ${formatCents(owed)} still owed with tax`,
          detail: `Finalizing carries ${openTabs.length === 1 ? "it" : "them"} over to be collected later.`,
          verdictSentence: `${openTabs.length === 1 ? "An open tab carries" : "Open tabs carry"} over when you finalize.`,
        },
  );

  const unpriceable = summary.unpriceableLines;
  checks.push(
    unpriceable.length === 0
      ? { key: "unpriceable", label: "Unpriceable lines", state: "pass", value: "0", phrase: "no unpriceable lines" }
      : {
          key: "unpriceable",
          label: "Unpriceable lines",
          state: "fail",
          value: String(unpriceable.length),
          phrase: "unpriceable lines",
          tone: "bad",
          chipText: "Unpriceable",
          title: count(unpriceable.length, "line needs a manager", "lines need a manager"),
          detail: "A line with no stored price must be confirmed before its tab totals correctly.",
          verdictSentence: "An unpriceable line needs a manager before its tab totals correctly.",
        },
  );

  const failed = summary.failedPayments;
  checks.push(
    failed.length === 0
      ? { key: "failed_payments", label: "Failed payments", state: "pass", value: "0", phrase: "no failed payments" }
      : {
          key: "failed_payments",
          label: "Failed payments",
          state: "fail",
          value: String(failed.length),
          phrase: "failed payments",
          tone: "bad",
          chipText: "Failed payment",
          title: count(failed.length, "failed payment", "failed payments"),
          detail: "Nothing was collected for it. Charge the guest again from the tab.",
          verdictSentence: "A failed payment is not collected until the guest is charged again.",
        },
  );

  const pending = summary.pendingReconciliationPayments;
  checks.push(
    pending.length === 0
      ? { key: "pending_payments", label: "Awaiting confirmation", state: "pass", value: "0", phrase: "nothing awaiting confirmation" }
      : {
          key: "pending_payments",
          label: "Awaiting confirmation",
          state: "fail",
          value: String(pending.length),
          phrase: "payments awaiting confirmation",
          tone: "bad",
          chipText: "Awaiting confirmation",
          title: count(pending.length, "payment awaiting confirmation", "payments awaiting confirmation"),
          detail: "Its tab cannot be paid again or closed until a manager confirms what happened.",
          verdictSentence: "A payment awaiting confirmation stops its tab from being paid again.",
        },
  );

  const outside = summary.outsideCollections;
  if (outside.unrecorded.length > 0) {
    checks.push({
      key: "outside_collections",
      label: "Collected outside",
      state: "fail",
      value: String(outside.unrecorded.length),
      phrase: "collected outside the product",
      tone: "bad",
      chipText: "Collected outside",
      title: count(outside.unrecorded.length, "walkout cleared as collected has no record", "walkouts cleared as collected have no record"),
      detail: "Record what was collected in the old processor before you finalize.",
      verdictSentence: "A walkout cleared as collected outside the product needs its record.",
    });
  } else if (outside.rows.length > 0) {
    checks.push({
      key: "outside_collections",
      label: "Collected outside",
      state: "info",
      value: `${formatCents(outside.collectedCents)} recorded`,
      phrase: `${formatCents(outside.collectedCents)} collected outside the product, recorded`,
      tone: "info",
      chipText: "Collected outside",
      title: `${formatCents(outside.collectedCents)} collected outside the product`,
      detail: "Recorded from the old processor. It is not part of Total collected.",
      showDetail: true,
    });
  } else {
    checks.push({ key: "outside_collections", label: "Collected outside", state: "pass", value: "0", phrase: "nothing collected outside the product" });
  }

  const activity = summary.activityByServiceDate;
  checks.push(
    activity.voidCount === 0
      ? { key: "voids", label: "Voids", state: "pass", value: "0", phrase: "no voids" }
      : {
          key: "voids",
          label: "Voids",
          state: "info",
          value: `${activity.voidCount}, ${formatCents(activity.voidValueCents)}`,
          phrase: count(activity.voidCount, "void", "voids"),
        },
  );

  const reopened = summary.reopenedTabActivity.length;
  checks.push(
    reopened === 0
      ? { key: "reopened", label: "Reopened tabs", state: "pass", value: "0", phrase: "no reopened tabs" }
      : { key: "reopened", label: "Reopened tabs", state: "info", value: String(reopened), phrase: count(reopened, "reopened tab", "reopened tabs") },
  );

  const readers = summary.terminalStatus.activeTerminals;
  const unhealthy = readers.filter((reader) => reader.health !== null && reader.health.state !== "online");
  if (unhealthy.length > 0) {
    checks.push({
      key: "readers",
      label: "Card readers",
      state: "fail",
      value: `${readers.length - unhealthy.length} of ${readers.length} online`,
      phrase: "card readers",
      tone: "warn",
      chipText: "Card reader",
      title: count(unhealthy.length, "card reader is not online", "card readers are not online"),
      detail: "Card payments cannot be taken on a reader that is not online.",
      verdictSentence: "A card reader is not online.",
    });
  } else {
    checks.push({
      key: "readers",
      label: "Card readers",
      state: "pass",
      value: readers.length === 0 ? "No readers" : `${readers.length} online`,
      phrase: readers.length === 0 ? "no card readers registered" : "card readers online",
    });
  }

  const sync = summary.syncStatus;
  if (sync.lastSucceededAt === null && sync.lastAttemptStatus === null) {
    // A location that has never run the client sync (it may not use MyTime) has nothing behind to review: a fact, not a problem.
    checks.push({ key: "sync", label: "Client sync", state: "info", value: "Never synced", phrase: "client sync never run" });
  } else if (sync.stale || sync.lastAttemptStatus === "failed") {
    checks.push({
      key: "sync",
      label: "Client sync",
      state: "fail",
      value: "Stale",
      phrase: "client sync",
      tone: "warn",
      chipText: "Client sync",
      title: sync.lastAttemptStatus === "failed" ? "The latest client directory sync failed" : "The client directory sync is behind",
      detail: "New guests from MyTime may be missing until it syncs again.",
      verdictSentence: "The client sync is behind.",
    });
  } else {
    checks.push({ key: "sync", label: "Client sync", state: "pass", value: "Fresh", phrase: "client sync fresh" });
  }

  const settlement = summary.settlement;
  if (settlement !== null) {
    if (settlement.clean) {
      checks.push({ key: "settlement", label: "Settlement", state: "pass", value: "Matches", phrase: "settlement matches" });
    } else {
      const waiting = settlement.status === "not_fetched" || settlement.status === "incomplete";
      checks.push({
        key: "settlement",
        label: "Settlement",
        state: "fail",
        value: waiting ? "Not fetched yet" : "Differs",
        phrase: "settlement",
        tone: waiting ? "info" : "bad",
        chipText: "Settlement",
        title: waiting ? "Card settlement has not been fetched yet" : "Card settlement does not match what was recorded",
        detail: waiting ? "It appears here after the next fetch." : "Declare the difference with a reason when you finalize.",
        verdictSentence: waiting ? "Card settlement has not been fetched yet." : "Card settlement differs from what was recorded.",
      });
    }
  }

  return checks;
}

export function buildCloseoutVerdict(checks: CloseoutCheck[]): CloseoutVerdict {
  const fails = checks.filter((check) => check.state === "fail");
  if (fails.length === 0) {
    const openTabsPassed = checks.some((check) => check.key === "open_tabs" && check.state === "pass");
    return {
      failCount: 0,
      tone: "good",
      chipSymbol: "✓",
      chipText: "All clear",
      title: "Nothing blocks finalizing",
      help: `${openTabsPassed ? "No open tabs, so nothing carries over." : "Nothing carries over."} Every check passed.`,
    };
  }
  const n = fails.length;
  const word = n < NUMBER_WORDS.length ? (NUMBER_WORDS[n] as string) : String(n);
  const sentences = fails
    .map((check) => check.verdictSentence)
    .filter((sentence): sentence is string => sentence !== undefined)
    .slice(0, 2);
  return {
    failCount: n,
    tone: fails.some((check) => check.tone === "bad") ? "bad" : "warn",
    chipSymbol: "!",
    chipText: `${n} need${n === 1 ? "s" : ""} a look`,
    title: n === 1 ? "One thing to review before you finalize" : `${word} things to review before you finalize`,
    help: sentences.join(" "),
  };
}

/** The one line that stands in for the passed checks when exceptions are showing: "6 other checks passed: no voids, ...". */
export function passedSummaryLine(checks: CloseoutCheck[]): string {
  const passed = checks.filter((check) => check.state !== "fail");
  if (passed.length === 0) return "";
  return `${passed.length} other ${passed.length === 1 ? "check" : "checks"} passed: ${passed.map((check) => check.phrase).join(", ")}.`;
}
